import { Card, CardContent } from '@creanizer/ui'
import type { CharacterData, GameState } from '@creanizer/types'

interface CharacterStatusDisplayProps {
  gameState: GameState
  alwaysShowParty?: boolean
}

export function CharacterStatusDisplay({ gameState, alwaysShowParty = true }: CharacterStatusDisplayProps) {
  const { characters } = gameState
  
  if (!characters) return null
  
  // アクティブなパーティメンバーを取得
  const activePartyMembers = characters.party?.filter(c => c.isActive) || []
  
  // 表示すべきキャラクターがいない場合
  if (!characters.player && activePartyMembers.length === 0) {
    return null
  }
  
  return (
    <div className="space-y-2">
      {/* プレイヤーステータス */}
      {characters.player && (
        <CharacterCard 
          character={characters.player} 
          isPlayer={true}
        />
      )}
      
      {/* パーティメンバーステータス（同行中のみ） */}
      {alwaysShowParty && activePartyMembers.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-gray-600 font-medium">パーティ</div>
          {activePartyMembers
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
            .map(member => (
              <CharacterCard 
                key={member.id} 
                character={member}
                isCompact={true}
              />
            ))}
        </div>
      )}
    </div>
  )
}

interface CharacterCardProps {
  character: CharacterData
  isPlayer?: boolean
  isCompact?: boolean
}

function CharacterCard({ character, isPlayer = false, isCompact = false }: CharacterCardProps) {
  const mainParams = ['health', 'hp', 'stamina', 'mp', 'magic', 'hunger', 'sanity']
  
  // 表示するパラメータを抽出
  const displayParams = Object.entries(character.parameters || {})
    .filter(([key]) => {
      const keyLower = key.toLowerCase()
      return mainParams.some(mp => keyLower.includes(mp))
    })
  
  if (isCompact) {
    return (
      <div className="p-2 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-1">
          <div className="font-medium text-sm">{character.name}</div>
          {character.status && character.status.length > 0 && (
            <div className="text-xs text-orange-600">
              {character.status.join(', ')}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-1 text-xs">
          {displayParams.map(([key, value]) => (
            <ParameterBar 
              key={key} 
              name={key} 
              value={value} 
              compact={true}
            />
          ))}
        </div>
      </div>
    )
  }
  
  return (
    <Card className={isPlayer ? 'border-blue-300' : ''}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-medium">
            {isPlayer && <span className="text-blue-600 mr-1">★</span>}
            {character.name}
          </div>
          {character.status && character.status.length > 0 && (
            <div className="text-xs text-orange-600">
              {character.status.join(', ')}
            </div>
          )}
        </div>
        
        <div className="space-y-1">
          {displayParams.map(([key, value]) => (
            <ParameterBar key={key} name={key} value={value} />
          ))}
        </div>
        
        {character.equipment && character.equipment.length > 0 && (
          <div className="mt-2 pt-2 border-t">
            <div className="text-xs text-gray-600">
              装備: {character.equipment.join(', ')}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface ParameterBarProps {
  name: string
  value: any
  compact?: boolean
}

function ParameterBar({ name, value, compact = false }: ParameterBarProps) {
  // 数値型パラメータの場合、バー表示
  if (typeof value === 'object' && 'current' in value && 'max' in value) {
    const percentage = (value.current / value.max) * 100
    const color = percentage > 50 ? 'bg-green-500' : percentage > 25 ? 'bg-yellow-500' : 'bg-red-500'
    
    if (compact) {
      return (
        <div className="flex items-center gap-1">
          <span className="text-xs">{getDisplayName(name)}:</span>
          <span className="text-xs font-medium">{value.current}/{value.max}</span>
        </div>
      )
    }
    
    return (
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span>{getDisplayName(name)}</span>
          <span>{value.current} / {value.max}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`${color} h-2 rounded-full transition-all`}
            style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
          />
        </div>
      </div>
    )
  }
  
  // 単純な数値の場合
  if (typeof value === 'number') {
    return (
      <div className="flex justify-between text-xs">
        <span>{getDisplayName(name)}</span>
        <span className="font-medium">{value}</span>
      </div>
    )
  }
  
  return null
}

function getDisplayName(key: string): string {
  const displayNames: Record<string, string> = {
    health: 'HP',
    hp: 'HP',
    stamina: 'スタミナ',
    mp: 'MP',
    magic: '魔力',
    hunger: '空腹度',
    sanity: '正気度',
    strength: '力',
    defense: '防御',
    speed: '素早さ'
  }
  
  return displayNames[key.toLowerCase()] || key
}